import styled, { DefaultTheme } from 'styled-components';
import { TRADING_VARIANTS, TradingVariant } from '../../fundamentals/colours/variants';
import { PRICE_MOVEMENT, PriceMovement } from '../Delta/Delta';
import { BaseDiv } from '../../common/BaseElements';

export interface ILineData {
  time: number;
  value: number;
}

const SPARK_SIZE_RATIO = 0.4;
const CANVAS_VERTICAL_BUFFER = 4; // if a horizontal line is drawn at the top or bottom of the canvas it will be cut off without this buffer

const movementMap: Record<PriceMovement, TradingVariant> = {
  [PRICE_MOVEMENT.UP]: TRADING_VARIANTS.POSITIVE,
  [PRICE_MOVEMENT.DOWN]: TRADING_VARIANTS.NEGATIVE,
  [PRICE_MOVEMENT.NO_CHANGE]: TRADING_VARIANTS.POSITIVE,
  [PRICE_MOVEMENT.UNKNOWN]: TRADING_VARIANTS.UNCHANGED
};

const getDataInfo = (data: ILineData[], totalWidth: number, timeRange: number, lineWidth: number, priceLineValue?: number) => {
  const segmentWidth = ((totalWidth - lineWidth) / data.length) * timeRange;
  let maxValue = data.reduce((accumulator, { value }) => Math.max(accumulator, value), 0);
  let minValue = data.reduce((accumulator, { value }) => Math.min(accumulator, value), maxValue);

  if (priceLineValue !== undefined) {
    maxValue = Math.max(maxValue, priceLineValue);
    minValue = Math.min(minValue, priceLineValue);
  }

  return { maxValue, minValue, segmentWidth };
};

export const makeCanvasHeight = (width: number) => width * SPARK_SIZE_RATIO + CANVAS_VERTICAL_BUFFER;

export const SparkContainer = styled(BaseDiv)`
  display: inline-block;
  height: 100%;
  width: 100%;
`;

export const convertRawChartToLineData = (data: string[]): ILineData[] =>
  data
    .map((d) => d.split('|'))
    .map((split) => ({
      time: new Date(`${split[0]}Z`).getTime() / 1000,
      value: Number.parseFloat(split[1])
    }));

export const getStatsToColour = (priceLineValue: number | undefined, data: ILineData[], theme: DefaultTheme) => {
  if (priceLineValue === undefined) {
    return theme.colours.trading[movementMap[PRICE_MOVEMENT.UP]];
  }

  const first = priceLineValue;
  const last = data.at(-1)?.value ?? /* istanbul ignore next */ 0;

  /* istanbul ignore next */
  if (first === last) {
    return theme.colours.trading[movementMap[PRICE_MOVEMENT.NO_CHANGE]];
  }

  /* istanbul ignore next */
  return theme.colours.trading[movementMap[first < last ? PRICE_MOVEMENT.UP : PRICE_MOVEMENT.DOWN]];
};

export const getMovementToColour = (theme: DefaultTheme, lastPriceMovement: PriceMovement) => {
  return theme.colours.trading[movementMap[lastPriceMovement]];
};

export const drawSpark = (
  canvas: HTMLCanvasElement | null,
  containerWidth: number,
  data: ILineData[],
  colour: string,
  lineWidth: number,
  timeRange: number,
  priceLineValue?: number
) => {
  if (!canvas) return;
  const ratio = window.devicePixelRatio;
  const context = canvas.getContext('2d')!;
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.strokeStyle = colour;
  context.lineWidth = lineWidth;
  const { maxValue, minValue, segmentWidth } = getDataInfo(data, containerWidth, timeRange, lineWidth, priceLineValue);
  const heightRange = maxValue - minValue;

  for (const [index, { value }] of data.entries()) {
    const x = index * segmentWidth + lineWidth / 2;
    const y = ((maxValue - value) / heightRange) * (containerWidth * SPARK_SIZE_RATIO - lineWidth) + lineWidth / 2;
    if (index === 0) {
      context.beginPath();
      context.moveTo(x, y);
    } else {
      context.lineTo(x, y);
    }
  }
  context.clearRect(0, 0, containerWidth, containerWidth * SPARK_SIZE_RATIO);

  context.stroke();

  if (priceLineValue !== undefined) {
    context.setLineDash([2, 1]);
    context.lineWidth = 1;
    const y = ((maxValue - priceLineValue) / heightRange) * (containerWidth * SPARK_SIZE_RATIO);
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(containerWidth, y);
    context.stroke();
    context.setLineDash([]);
  }
};

export const drawSparkSvg = (containerWidth: number, data: ILineData[], colour: string, lineWidth: number, timeRange: number, priceLineValue?: number) => {
  const { maxValue, minValue } = getDataInfo(data, containerWidth, timeRange, lineWidth, priceLineValue);
  const height = containerWidth * SPARK_SIZE_RATIO;
  const heightRange = maxValue - minValue;
  const priceLineY = ((maxValue - (priceLineValue ?? 0)) / heightRange) * (containerWidth * SPARK_SIZE_RATIO);

  // Create line points
  const points = data
    .map((item, index) => {
      const x = (index / (data.length - 1)) * (containerWidth * timeRange);
      const y = height - ((item.value - minValue) / (maxValue - minValue)) * height;
      return `${x},${y}`;
    })
    .join(' ');

  const priceLine = priceLineValue
    ? `<line
      x1="0"
      y1="${priceLineY}"
      x2="${containerWidth}"
      y2="${priceLineY}"
      stroke="${colour}"
      stroke-width="1"
      stroke-dasharray="2,1"
  />`
    : '';

  return `
  <svg width="${containerWidth}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <polyline 
          fill="none" 
          stroke="${colour}" 
          stroke-width="${lineWidth}" 
          points="${points}" 
      />
      ${priceLine}
  </svg>
  `;
};
